/**
 * AiSpendStrip — what the AI layer is costing, on the page that spends it.
 *
 * WHY THIS EXISTS
 *
 * The AI Desk showed exactly one cost figure: `run.costUsd`, the price of the single run being
 * displayed. Today's total, the month to date and where the month is heading appeared only on the
 * Dashboard card — so the page with the two billed buttons on it was the one page that never said
 * what the month was costing.
 *
 * WHAT THE PROJECTION IS AND IS NOT
 *
 * Month-to-date divided by elapsed days, times the days in the month. It is labelled a projection
 * everywhere it appears and it is never compared against the cap, because the cap is a DAILY cap
 * and presenting a monthly projection next to it would invite exactly the wrong comparison. A
 * straight-line run rate is not a forecast; it assumes tomorrow looks like the last fortnight.
 *
 * CURRENCY
 *
 * Rupees, like every other billed control in the app (`inr`). The dollar figure is the ledger's
 * own unit and is kept in the tooltip so a reader reconciling against Anthropic's console can find
 * it. Mixing the two in one row — which this page used to do, `$0.978` beside `Run now · ₹61.2` —
 * makes two numbers look unrelated when one is the other.
 */
import React from 'react';
import { Box, Typography, Tooltip, LinearProgress } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAppTheme } from '../../hooks/useAppTheme';
import { useAiCost, inr } from '../../hooks/useAiCost';
import HelpLink from '../common/HelpLink';

/** Month-to-date and the straight-line projection, from the ledger's own monthly rollup. */
export function projectMonth(report: { spend: { byMonth: Array<{ month: string; costUsd: number }> } } | null): {
  mtdUsd: number | null; projectedUsd: number | null; elapsedDays: number; daysInMonth: number;
} {
  const now = new Date();
  const key = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
  const row = report?.spend?.byMonth?.find(m => m.month === key) || null;
  const daysInMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).getUTCDate();
  const elapsedDays = now.getUTCDate();
  if (!row) return { mtdUsd: null, projectedUsd: null, elapsedDays, daysInMonth };
  const mtdUsd = Number(row.costUsd) || 0;
  // Guard the first of the month: dividing by zero elapsed days would project infinity.
  const projectedUsd = elapsedDays > 0 ? (mtdUsd / elapsedDays) * daysInMonth : null;
  return { mtdUsd, projectedUsd, elapsedDays, daysInMonth };
}

const Cell: React.FC<{ label: string; value: string; hint: string; color?: string }> =
  ({ label, value, hint, color }) => {
    const { C } = useAppTheme();
    return (
      <Tooltip arrow title={hint}>
        <Box sx={{ minWidth: 96, cursor: 'help' }}>
          <Typography variant="caption" sx={{ color: C.dim, display: 'block', lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: color || 'text.primary' }}>
            {value}
          </Typography>
        </Box>
      </Tooltip>
    );
  };

const AiSpendStrip: React.FC = () => {
  const { C } = useAppTheme();
  const { report } = useAiCost();
  if (!report) return null;

  const { mtdUsd, projectedUsd, elapsedDays, daysInMonth } = projectMonth(report);
  const today = report.spend.todayUsd;
  const cap = report.guard.capUsd;
  const capPct = cap > 0 ? Math.min(100, (today / cap) * 100) : 0;
  const note = report.notes?.[0] || null;

  return (
    <Box sx={{
      p: 1.2, mb: 2, borderRadius: 1, border: `1px solid ${C.accentBorder}`, bgcolor: C.accentMuted,
    }}>
      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Cell
          label="Spent today"
          value={inr(today)}
          hint={`${inr(today)} (${today.toFixed(3)} USD) billed since 00:00 UTC. The ledger's day is UTC, so a late-evening Indian session counts against the same day the nightly run did.`}
        />
        <Cell
          label="Month to date"
          value={inr(mtdUsd)}
          hint={mtdUsd == null
            ? 'No calls billed this month yet.'
            : `${inr(mtdUsd)} (${mtdUsd.toFixed(3)} USD) over the ${elapsedDays} days of this month so far.`}
        />
        <Cell
          label="Projected month"
          value={projectedUsd == null ? '—' : inr(projectedUsd)}
          color={C.dim}
          hint={projectedUsd == null
            ? 'Nothing billed this month yet, so there is no run rate to project from.'
            : `Month-to-date divided by the ${elapsedDays} days elapsed, times ${daysInMonth} days. A straight-line run rate, not a forecast — it assumes the rest of the month looks like the part already spent.`}
        />
        <Cell
          label="Daily cap"
          value={inr(cap)}
          color={report.guard.capReached ? C.bear : undefined}
          hint={report.guard.capReached
            ? `Today's cap of ${inr(cap)} has been reached. Nothing further will be submitted until 00:00 UTC. Raise it in Settings → AI.`
            : `A hard daily backstop. When today's spend reaches ${inr(cap)} every billed path refuses before it calls the model. This is a DAILY cap — the monthly projection beside it is not measured against it.`}
        />
        <Cell
          label="Model"
          value={report.model.selected || '—'}
          hint={`${report.model.price
            ? `${report.model.price.inPerM} USD per million input tokens, ${report.model.price.outPerM} out. `
            : 'No published rate on file for this model. '}${report.model.priceBasis || ''}${report.model.priceEstimated ? ' — the rate was inferred from the model family, not read from a published table.' : ''}${report.model.ceiling ? ` Tier ceiling: ${report.model.ceiling}.` : ''}`}
        />
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <Typography variant="caption" sx={{ color: C.dim, display: 'block', lineHeight: 1.2 }}>
            Today against the cap
          </Typography>
          <LinearProgress variant="determinate" value={capPct}
            sx={{
              mt: 0.6, height: 6, borderRadius: 3,
              '& .MuiLinearProgress-bar': { bgcolor: capPct >= 100 ? C.bear : capPct >= 70 ? C.warn : C.bull },
            }} />
          <Typography variant="caption" sx={{ color: C.dim }}>
            {Math.round(capPct)}% of {inr(cap)}
          </Typography>
        </Box>
        <HelpLink to="ai-desk/cost" variant="icon" label="How AI spend is measured and capped" />
      </Box>

      {/* The 8/31 pricing caveat travels with the numbers rather than living only in a doc. */}
      {note && (
        <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'flex-start', mt: 0.8 }}>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: C.dim, mt: 0.2 }} />
          <Typography variant="caption" sx={{ color: C.dim, lineHeight: 1.4 }}>{note}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default AiSpendStrip;
